import React from 'react'
import StatisticsCourseSummary from "../sections/StatisticsCourseSummary";
import StatisticsPlaylistSummary from "../sections/StatisticsPlaylistSummary";
import StatisticsScoredSceneSummary from "../sections/StatisticsScoredSceneSummary";
import StatisticsUserSummary from "../sections/StatisticsUserSummary";
import {useConfirm} from "material-ui-confirm";
import {useStyles} from "../sections/StatisticsSummary.css";
import {useIntl} from "react-intl";

const useStatisticsSummary = () => {
    const confirm = useConfirm();
    const classes = useStyles()

    // Internationalization for simple strings
    const intl = useIntl();

    // Source: Idea from https://stackoverflow.com/questions/36098913/convert-seconds-to-days-hours-minutes-and-seconds
    const formatDuration = (seconds) => {
        const h = Math.floor(seconds / 3600)
        const m = Math.floor(seconds % 3600 / 60)
        const s = Math.floor(seconds % 3600 % 60)

        const hh = h > 0 ? (h > 9 ? h : ("0" + h)) : "00"
        const mm = m > 0 ? (m > 9 ? m : ("0" + m)) : "00"
        const ss = s > 0 ? (s > 9 ? s : ("0" + s)) : "00"
        return `${hh}:${mm}:${ss}`
    }

    const handleStatisticsSummary = (value) => {

        let metric = value.metric
        let aggregation = value.entity
        let chartType = value.chartType
        let entityId = value.entityId
        let label = value.label
        let resultPositive = value.correct || value.reactionTime || value.count || value.countClosed
        let resultNegative = value.wrong || value.countOpen

        // Prepare extended description to be shown
        let title = ''
        let description = intl.formatMessage({
            id: 'app.statistics.additionalInfo.noInfoAvailable',
            defaultMessage: 'No additional information available for this chart.'
        })

        // Header information dependent on metric and chart type (independent of aggregation - ONLY RESULT INFORMATION)
        if (metric === 'decisionAccuracy') {
            if (chartType === 'total') {
                title = <div className={classes.row}>
                    <div className={classes.dialogTitleGreen}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.decisionAccuracy.total.title.left',
                            defaultMessage: 'Correct: {numberCorrectDecisions}'
                        }, {numberCorrectDecisions: resultPositive})}
                    </div>
                    <div className={classes.dialogTitleRed}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.decisionAccuracy.total.title.right',
                            defaultMessage: 'Wrong: {numberWrongDecisions}'
                        }, {numberWrongDecisions: resultNegative})}
                    </div>
                </div>
            }

            if (chartType === 'average') {
                title = <div className={classes.row}>
                    <div className={classes.dialogTitleGreen}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.decisionAccuracy.average.title.left',
                            defaultMessage: 'Correct: {percentageCorrectDecisions} %'
                        }, {percentageCorrectDecisions: resultPositive})}
                    </div>
                    <div className={classes.dialogTitleRed}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.decisionAccuracy.average.title.right',
                            defaultMessage: 'Wrong: {percentageNegativeDecisions} %'
                        }, {percentageNegativeDecisions: resultNegative})}
                    </div>
                </div>
            }

            if (chartType === 'accumulated') {
                title = <div className={classes.row}>
                    <div className={classes.dialogTitleGreen}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.decisionAccuracy.accumulated.title.left',
                            defaultMessage: 'Correct at {date}: {percentageCorrectDecisions} %'
                        }, {date: label, percentageCorrectDecisions: resultPositive})}
                    </div>
                </div>
            }
        }

        if (metric === 'reactionTime') {
            if (chartType === 'average') {
                title = <div className={classes.row}>
                    <div className={classes.dialogTitleGreen}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.reactionTime.average.title.left',
                            defaultMessage: 'Reaction Time: {reactionTime}s'
                        }, {reactionTime: resultPositive})}
                    </div>
                </div>
            }

            if (chartType === 'accumulated') {
                title = <div className={classes.row}>
                    <div className={classes.dialogTitleGreen}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.reactionTime.accumulated.title.left',
                            defaultMessage: 'Reaction Time at {date}: {reactionTime}s'
                        }, {date: label, reactionTime: resultPositive})}
                    </div>
                </div>
            }
        }

        if (metric === 'trainingIntensity') {
            if (chartType === 'total') {
                title = <div className={classes.row}>
                    <div className={classes.dialogTitleGreen}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.trainingIntensity.total.title.left',
                            defaultMessage: 'Training Intensity: {time}'
                        }, {time: formatDuration(resultPositive * 60)})}
                    </div>
                </div>
            }

            if (chartType === 'average') {
                title = <div className={classes.row}>
                    <div className={classes.dialogTitleGreen}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.trainingIntensity.average.title.left',
                            defaultMessage: 'Avg. Training Intensity / day: {time}'
                        }, {time: formatDuration(resultPositive * 60)})}
                    </div>
                </div>
            }

            if (chartType === 'accumulated') {
                title = <div className={classes.row}>
                    <div className={classes.dialogTitleGreen}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.trainingIntensity.accumulated.title.left',
                            defaultMessage: 'Avg. Training Intensity at {date}: {time}'
                        }, {date: label, time: formatDuration(resultPositive * 60)})}
                    </div>
                </div>
            }
        }

        if (metric === 'createdScoredScenes') {
            if (chartType === 'total') {
                title = <div className={classes.row}>
                    <div className={classes.dialogTitleGreen}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.createdScoredScenes.total.title.left',
                            defaultMessage: 'Created Scenes: {numberScenes}'
                        }, {numberScenes: resultPositive})}
                    </div>
                </div>
            }

            if (chartType === 'accumulated') {
                title = <div className={classes.row}>
                    <div className={classes.dialogTitleGreen}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.createdScoredScenes.accumulated.title.left',
                            defaultMessage: 'Created Scenes at {date}: {numberScenes}'
                        }, {date: label, numberScenes: resultPositive})}
                    </div>
                </div>
            }
        }

        if (metric === 'createdUsers') {
            if (chartType === 'total') {
                title = <div className={classes.row}>
                    <div className={classes.dialogTitleGreen}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.createdUsers.total.title.left',
                            defaultMessage: 'Created Users: {numberUsers}'
                        }, {numberUsers: resultPositive})}
                    </div>
                </div>
            }

            if (chartType === 'accumulated') {
                title = <div className={classes.row}>
                    <div className={classes.dialogTitleGreen}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.createdUsers.accumulated.title.left',
                            defaultMessage: 'Created Users at {date}: {numberUsers}'
                        }, {date: label, numberUsers: resultPositive})}
                    </div>
                </div>
            }
        }

        if (metric === 'createdChallengedDecisions') {
            if (chartType === 'total') {
                title = <div className={classes.row}>
                    <div className={classes.dialogTitleGreen}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.createdChallengedDecisions.total.title.left',
                            defaultMessage: 'Closed: {numberChallengedDecisions}'
                        }, {numberChallengedDecisions: resultPositive})}
                    </div>
                    <div className={classes.dialogTitleRed}>
                        {intl.formatMessage({
                            id: 'app.statistics.additionalInfo.createdChallengedDecisions.total.title.right',
                            defaultMessage: 'Open: {numberChallengedDecisions}'
                        }, {numberChallengedDecisions: resultNegative})}
                    </div>
                </div>
            }
        }

        // Additional information for selected aggregation levels (independent of metric and chart type - ONLY ENTITY INFORMATION)
        if (aggregation === 'course') {
            description = <StatisticsCourseSummary courseKey={entityId}/>
        }
        if (aggregation === 'playlist') {
            description = <StatisticsPlaylistSummary playlistKey={entityId}/>
        }
        if (aggregation === 'scoredScene') {
            description = <StatisticsScoredSceneSummary scoredSceneKey={entityId}/>
        }
        if (aggregation === 'user') {
            description = <StatisticsUserSummary userUid={entityId}/>
        }

        // Show the additional information in a confirm dialog
        confirm({
            title: title,
            content: description
        })
            .then(() => null)
            .catch(() => null)
    }
    return {handleStatisticsSummary}
}
export default useStatisticsSummary;
